/**
 * Reusable file picker for tools that need to select a file from
 * the user's stored library OR upload a new one.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import DropZone from "./DropZone";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function FilePicker({ onSelected, accept = "application/pdf", filter }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("upload"); // upload | library
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (tab !== "library") return;
    setLoading(true);
    api
      .get("/files")
      .then(({ data }) => {
        const list = filter ? data.filter(filter) : data;
        setFiles(list);
      })
      .catch(async (e) => {
        // Fall back to IndexedDB cache when offline
        try {
          const { getCachedFiles } = await import("../lib/offline");
          const cached = await getCachedFiles("__any__"); // userId not stored here, get all
          // The cache stores files with user_id field; here we just show all available
          const all = (cached || []).filter(filter || (() => true));
          if (all.length) {
            setFiles(all);
            return;
          }
        } catch {}
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      })
      .finally(() => setLoading(false));
  }, [tab, filter]);

  async function uploadAndSelect(file) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd);
      onSelected(data, file);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div data-testid="file-picker">
      <div className="flex border border-zinc-200 rounded-sm w-fit overflow-hidden mb-4">
        <button
          className={`px-4 py-2 text-xs uppercase tracking-wider ${tab === "upload" ? "bg-black text-white" : "bg-white text-zinc-700"}`}
          onClick={() => setTab("upload")}
          data-testid="picker-tab-upload"
        >
          Upload
        </button>
        <button
          className={`px-4 py-2 text-xs uppercase tracking-wider ${tab === "library" ? "bg-black text-white" : "bg-white text-zinc-700"}`}
          onClick={() => setTab("library")}
          data-testid="picker-tab-library"
        >
          Library
        </button>
      </div>

      {tab === "upload" ? (
        <DropZone accept={accept} onFiles={(fs) => uploadAndSelect(fs[0])} testId="picker-dropzone" />
      ) : (
        <div className="border border-zinc-200 rounded-md p-3 bg-white max-h-[440px] overflow-auto">
          {loading && <div className="text-zinc-500 text-sm p-4">{t("auth_loading")}</div>}
          {!loading && files.length === 0 && (
            <div className="text-zinc-500 text-sm p-4">{t("dashboard_empty")}</div>
          )}
          <ul className="grid gap-2">
            {files.map((f) => (
              <li key={f.id}
                className="flex items-center justify-between border border-zinc-100 rounded-sm px-4 py-2 hover:bg-zinc-50">
                <span className="text-sm font-medium truncate max-w-[60%]">{f.original_filename}</span>
                <Button
                  variant="ghost"
                  className="rounded-sm h-8 px-3"
                  onClick={() => onSelected(f, null)}
                  data-testid={`picker-select-${f.id}`}
                >
                  {t("cta_continue")} →
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {busy && <div className="text-xs text-zinc-500 mt-2">{t("uploading")}</div>}
    </div>
  );
}
